import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@context/AuthContext'
import Layout from '@components/layout/Layout'
import Login from '@pages/auth/Login'
import ForgotPassword from '@pages/auth/ForgotPassword'
import ResetPassword from '@pages/auth/ResetPassword'
import AcceptInvitation from '@pages/auth/AcceptInvitation'
import Dashboard from '@pages/Dashboard'
import AssetsList from '@pages/assets/AssetsList'
import AssetDetail from '@pages/assets/AssetDetail'
import AssetDetailPrint from '@pages/assets/AssetDetailPrint'
import AssetScanner from '@pages/assets/AssetScanner'
import TransfersList from '@pages/assets/TransfersList'
import RequisitionsList from '@pages/assets/RequisitionsList'
import ExternalRepairsList from '@pages/assets/ExternalRepairsList'
import VehiclesList from '@pages/assets/VehiclesList'
import FaultReportsList from '@pages/assets/FaultReportsList'
import VehicleRequestsList from '@pages/assets/VehicleRequestsList'
import VehicleRequestPrint from '@pages/assets/VehicleRequestPrint'
import AssetSettings from '@pages/assets/AssetSettings'
import WorkOrdersList from '@pages/maintenance/WorkOrdersList'
import WorkOrderDetail from '@pages/maintenance/WorkOrderDetail'
import WorkOrderPrint from '@pages/maintenance/WorkOrderPrint'
import PMScheduleList from '@pages/maintenance/PMScheduleList'
import PMDashboard from '@pages/maintenance/PMDashboard'
import CalibrationsList from '@pages/maintenance/CalibrationsList'
import JobCardsList from '@pages/maintenance/JobCardsList'
import JobCardPrint from '@pages/maintenance/JobCardPrint'
import PMGanttPrint from '@pages/maintenance/PMGanttPrint'
import InventoryList from '@pages/inventory/InventoryList'
import InventoryCategories from '@pages/inventory/InventoryCategories'
import IssueVouchersList from '@pages/inventory/IssueVouchersList'
import IssueVoucherPrint from '@pages/inventory/IssueVoucherPrint'
import VendorsList from '@pages/vendors/VendorsList'
import ContractsList from '@pages/vendors/ContractsList'
import FacilitiesList from '@pages/facilities/FacilitiesList'
import ReportsList from '@pages/reports/ReportsList'
import NotificationsList from '@pages/notifications/NotificationsList'
import BroadcastNotifications from '@pages/notifications/BroadcastNotifications'
import EquipmentLogsList from '@pages/radiation-oncology/EquipmentLogsList'
import QARecordsList from '@pages/radiation-oncology/QARecordsList'
import RadiationSourcesList from '@pages/radiation-oncology/RadiationSourcesList'
import LabResultsList from '@pages/clinical/LabResultsList'
import ImagingResultsList from '@pages/clinical/ImagingResultsList'
import AuditLogsList from '@pages/admin/AuditLogsList'
import Administration from '@pages/admin/Administration'
import Settings from '@pages/Settings'
import DocsPage from '@pages/docs/DocsPage'
import CalendarPage from '@pages/CalendarPage'
import PresentationPage from '@pages/PresentationPage'
import NotFound from '@pages/NotFound'
import ListExportPreview from '@pages/common/ListExportPreview'
import DocumentVerification from '@pages/verify/DocumentVerification'
import VerifyLanding from '@pages/verify/VerifyLanding'
import { CircularProgress, Box } from '@mui/material'

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress color="primary" />
      </Box>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return children
}

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress color="primary" />
      </Box>
    )
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

function App() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />

      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />

      <Route
        path="/accept-invitation"
        element={
          <PublicRoute>
            <AcceptInvitation />
          </PublicRoute>
        }
      />

      {/* Document verification - handles its own auth redirect with return URL */}
      <Route path="/verify/:code" element={<DocumentVerification />} />

      {/* Fully public page - no auth check */}
      <Route path="/presentation" element={<PresentationPage />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        {/* Analytics now lives inside the Dashboard; keep the path as a redirect. */}
        <Route path="analytics" element={<Navigate to="/dashboard" replace />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="export/preview" element={<ListExportPreview />} />

        {/* Assets */}
        <Route path="assets" element={<AssetsList />} />
        <Route path="assets/scan" element={<AssetScanner />} />
        <Route path="assets/transfers" element={<TransfersList />} />
        <Route path="assets/requisitions" element={<RequisitionsList />} />
        <Route path="assets/external-repairs" element={<ExternalRepairsList />} />
        <Route path="assets/vehicles" element={<VehiclesList />} />
        <Route path="assets/fault-reports" element={<FaultReportsList />} />
        <Route path="assets/vehicle-requests" element={<VehicleRequestsList />} />
        <Route path="assets/vehicle-requests/:id/print" element={<VehicleRequestPrint />} />
        <Route path="assets/settings" element={<AssetSettings />} />
        <Route path="assets/:id" element={<AssetDetail />} />
        <Route path="assets/:id/print" element={<AssetDetailPrint />} />

        {/* Maintenance */}
        <Route path="work-orders" element={<WorkOrdersList />} />
        <Route path="work-orders/:id" element={<WorkOrderDetail />} />
        <Route path="work-orders/:id/print" element={<WorkOrderPrint />} />
        <Route path="pm-schedule" element={<PMScheduleList />} />
        <Route path="pm-dashboard" element={<PMDashboard />} />
        <Route path="calibrations" element={<CalibrationsList />} />
        <Route path="maintenance/job-cards" element={<JobCardsList />} />
        <Route path="maintenance/job-cards/:id/print" element={<JobCardPrint />} />
        <Route path="maintenance/pm-schedule/print" element={<PMGanttPrint />} />

        {/* Inventory */}
        <Route path="inventory" element={<InventoryList />} />
        <Route path="inventory/categories" element={<InventoryCategories />} />
        <Route path="inventory/issue-vouchers" element={<IssueVouchersList />} />
        <Route path="inventory/issue-vouchers/:id/print" element={<IssueVoucherPrint />} />

        {/* Vendors */}
        <Route path="vendors" element={<VendorsList />} />
        <Route path="contracts" element={<ContractsList />} />

        {/* Radiation Oncology */}
        <Route path="radiation-oncology/equipment-logs" element={<EquipmentLogsList />} />
        <Route path="radiation-oncology/qa-records" element={<QARecordsList />} />
        <Route path="radiation-oncology/sources" element={<RadiationSourcesList />} />

        {/* Clinical Results */}
        <Route path="clinical/lab-results" element={<LabResultsList />} />
        <Route path="clinical/imaging-results" element={<ImagingResultsList />} />

        {/* Facilities */}
        <Route path="facilities" element={<FacilitiesList />} />

        {/* Reports */}
        <Route path="reports" element={<ReportsList />} />

        {/* Notifications */}
        <Route path="notifications" element={<NotificationsList />} />
        <Route path="notifications/broadcasts" element={<BroadcastNotifications />} />

        {/* Administration */}
        <Route path="administration/users" element={<Administration />} />
        <Route path="administration/departments" element={<Administration />} />
        <Route path="administration/roles" element={<Administration />} />
        <Route path="administration/settings" element={<Administration />} />
        <Route path="administration/audit-logs" element={<AuditLogsList />} />

        {/* Settings */}
        <Route path="settings" element={<Settings />} />

        {/* Documentation (in-app Help center) */}
        <Route path="docs/*" element={<DocsPage />} />
        <Route path="documentation" element={<Navigate to="/docs" replace />} />

        {/* Document Verification */}
        <Route path="verify" element={<VerifyLanding />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

export default App
